import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Breadcrumbs from 'components/Common/Breadcrumb';
import { PriceRangeSlabApi } from "apis/priceRangeSlab";
import Swal from "../../helpers/swal_helper";
function AddEditPriceRangeSlab(props) {
    const history = useHistory();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        slab_name: "",
        symbol: "",
        min_price: "",
        max_price: "",
        status: "true"
    });

    const validationSchema = Yup.object().shape({
        slab_name: Yup.string()
            .required(props.t("Slab name is required"))
            .min(3, props.t("Slab name must be at least 3 characters"))
            .max(50, props.t("Slab name must not exceed 50 characters")),
        min_price: Yup.number()
            .required(props.t("Minimum price is required"))
            .min(0, props.t("Minimum price must be greater than or equal to 0")),
        max_price: Yup.number()
            .required(props.t("Maximum price is required"))
            .min(0, props.t("Maximum price must be greater than or equal to 0"))
            .when('min_price', {
                is: (minPrice) => minPrice > 0,
                then: Yup.number().min(Yup.ref('min_price'), props.t("Maximum price must be greater than minimum price"))
            }),
        status: Yup.boolean()
            .required(props.t("Status is required"))
    });

    useEffect(() => {
        if (id) {
            getPriceRangeSlabById(id);
        }
    }, [id]);

    const getPriceRangeSlabById = (id) => {
        setLoading(true);
        PriceRangeSlabApi.fetchPriceRangeSlab(id)
            .then((res) => {
                if (res.data.status) {
                    const data = res.data.data;
                    setInitialValues({
                        ...data,
                        status: data.status ? "true" : "false"
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to fetch price range slab details",
                    icon: "error",
                });
            });
    };

    const handleSubmit = (values, { setSubmitting }) => {
        setLoading(true);

        const submitData = {
            ...values,
            min_price: Number(values.min_price),
            max_price: Number(values.max_price),
            status : values.status === "true"
        };

        const apiCall = id
            ? PriceRangeSlabApi.updatePriceRangeSlab({
                ...submitData,
                slab_id: id
            })
            : PriceRangeSlabApi.addPriceRangeSlab(submitData);

        apiCall
            .then((res) => {
                if (res.data.status) {
                    Swal.fire({
                        title: "Success",
                        text: res.data.message,
                        icon: "success",
                    }).then(() => {
                        history.push("/price-range-slab");
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
                setSubmitting(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                setSubmitting(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to save price range slab",
                    icon: "error",
                });
            });
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs
                    title={props.t("Dashboard")}
                    breadcrumbItem={id ? props.t("Edit Price Range Slab") : props.t("Add Price Range Slab")}
                />
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({ errors, touched }) => (
                                        <Form>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Slab Name")}</label>
                                                    <Field
                                                        type="text"
                                                        className={`form-control ${errors.slab_name && touched.slab_name ? 'is-invalid' : ''}`}
                                                        name="slab_name"
                                                    />
                                                    {errors.slab_name && touched.slab_name && (
                                                        <div className="invalid-feedback">{errors.slab_name}</div>
                                                    )}
                                                </div>
                                                {/* symbol fields */}
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Symbol")}</label>
                                                    <Field
                                                        type="text"
                                                        className={`form-control ${errors.symbol && touched.symbol ? 'is-invalid' : ''}`}
                                                        name="symbol"
                                                    />
                                                    {errors.symbol && touched.symbol && (
                                                        <div className="invalid-feedback">{errors.symbol}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Minimum Price")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.min_price && touched.min_price ? 'is-invalid' : ''}`}
                                                        name="min_price"
                                                    />
                                                    {errors.min_price && touched.min_price && (
                                                        <div className="invalid-feedback">{errors.min_price}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Maximum Price")}</label>
                                                    <Field
                                                        type="number"
                                                        className={`form-control ${errors.max_price && touched.max_price ? 'is-invalid' : ''}`}
                                                        name="max_price"
                                                    />
                                                    {errors.max_price && touched.max_price && (
                                                        <div className="invalid-feedback">{errors.max_price}</div>
                                                    )}
                                                </div>
                                                {/* <div className="col-md-6">
                                                    <label className="form-label">{props.t("Status")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.status && touched.status ? 'is-invalid' : ''}`}
                                                        name="status"
                                                    >
                                                        <option value={true}>{props.t("Active")}</option>
                                                        <option value={false}>{props.t("Inactive")}</option>
                                                    </Field>
                                                    {errors.status && touched.status && (
                                                        <div className="invalid-feedback">{errors.status}</div>
                                                    )}
                                                </div> */}
                                            </div>

                                            {/* <div className="row mb-3">
                                                
                                            </div> */}

                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                    >
                                                        {loading ? props.t("Saving...") : props.t("Save")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary ms-2"
                                                        onClick={() => history.push("/price-range-slab")}
                                                    >
                                                        {props.t("Cancel")}
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AddEditPriceRangeSlab.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AddEditPriceRangeSlab); 